import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
export interface NotificationChannel {
  channel_type: "telegram" | "whatsapp";
  channel_identifier?: string;
  is_verified: boolean;
  is_active: boolean;
}
export interface NotificationPreference {
  notification_type: string;
  is_enabled: boolean;
}
export interface NotificationStatusData {
  channels: NotificationChannel[];
  preferences: NotificationPreference[];
  capturedPhone?: string | null;
}
interface UseNotificationStatusOptions {
  /** Whether to fetch data (default: true) */
  enabled?: boolean;
  /**
   * Defer fetching until browser is idle (default: false)
   * When true, request won't block initial page load - fires after ~100ms idle
   */
  defer?: boolean;
}

/**
 * Fetches notification channel status (WhatsApp/Telegram verification, preferences).
 *
 * Uses React Query for automatic request deduplication - multiple components
 * can call this hook and only ONE request will be made.
 *
 * @param options.enabled - Whether to fetch (default: true)
 * @param options.defer - Defer until browser idle (default: false) - use for non-critical data
 *
 * @example
 * // Immediate fetch (for settings pages where data is needed)
 * const { data } = useNotificationStatus()
 *
 * // Deferred fetch (for dashboard where it's not critical)
 * const { data } = useNotificationStatus({ defer: true })
 */
export const useNotificationStatus = options => {
  const $ = _c(9);
  let t0;
  if ($[0] !== options) {
    t0 = options ?? {};
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    enabled: t1,
    defer: t2
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const defer = t2 === undefined ? false : t2;
  const [shouldFetch, setShouldFetch] = useState(!defer);
  let t3;
  let t4;
  if ($[2] !== defer || $[3] !== enabled) {
    t3 = () => {
      if (!defer || !enabled) {
        return;
      }
      if (typeof requestIdleCallback !== "undefined") {
        const id = requestIdleCallback(() => setShouldFetch(true), {
          timeout: 2000
        });
        return () => cancelIdleCallback(id);
      } else {
        const id_0 = setTimeout(() => setShouldFetch(true), 100);
        return () => clearTimeout(id_0);
      }
    };
    t4 = [defer, enabled];
    $[2] = defer;
    $[3] = enabled;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["notification-status"];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = enabled && shouldFetch;
  let t7;
  if ($[7] !== t6) {
    t7 = {
      staleTime: 300000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
      enabled: t6
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return useQuery(t5, _temp, t7);
};

/**
 * Lazy helper to check if user has any verified notification channel.
 * Defers the API call until browser is idle - perfect for non-blocking UI hints.
 *
 * Returns `false` while loading (optimistic default - most users don't have verified channel)
 */
export const useHasVerifiedChannel = options => {
  const $ = _c(8);
  const t0 = options?.enabled;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      defer: true,
      enabled: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    isLoading,
    isFetched
  } = useNotificationStatus(t1);
  let t2;
  if ($[2] !== data?.channels) {
    t2 = data?.channels?.some(_temp2) ?? false;
    $[2] = data?.channels;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isFetched || $[5] !== isLoading || $[6] !== t2) {
    t3 = {
      hasVerifiedChannel: t2,
      isLoading,
      isFetched
    };
    $[4] = isFetched;
    $[5] = isLoading;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
async function _temp() {
  const res = await fetch("/api/notifications/status");
  if (!res.ok) {
    throw new Error("Failed to fetch notification status");
  }
  return res.json();
}
function _temp2(c) {
  return c.is_verified;
}